import { useEffect, useRef, useState } from 'react';
import { FiEdit2, FiGift, FiTrash2, FiXCircle } from 'react-icons/fi';

import { isPaidTier, planSeatsLabel, tierLabel } from '@/admin/admin.plans';
import { useVhAdminStore, VH_ADMIN_PAGE_SIZE, vhAdminVesselHolder } from '@/admin/admin.store';
import type { VhAdminVessel } from '@/admin/admin.types';
import { AdminBadge } from '@/admin/components/AdminBadge';
import { AdminButton } from '@/admin/components/AdminButton';
import { AdminCard, AdminCardList } from '@/admin/components/AdminCard';
import { AdminEntityIdentity } from '@/admin/components/AdminEntityIdentity';
import { AdminForceDeleteModal } from '@/admin/components/AdminForceDeleteModal';
import { AdminModal } from '@/admin/components/AdminModal';
import { AdminPagination } from '@/admin/components/AdminPagination';
import { AdminSearchInput } from '@/admin/components/AdminSearchInput';
import { AdminSelect } from '@/admin/components/AdminSelect';
import { AdminSpinner } from '@/admin/components/AdminSpinner';
import {
  AdminTable,
  AdminTableCell,
  AdminTableHead,
  AdminTableHeadCell,
  AdminTableRow,
} from '@/admin/components/AdminTable';
import styles from '@/admin/pages/AllVesselsPage.module.scss';
import { isSuperAdminRole, useVhAdminAccessStore } from '@/admin/vh-admin-access.store';
import { isSeatsBelowOwnerGroup } from '@/common/error';
import {
  PAID_TIERS,
  SubscriptionTier,
  type PaidSubscriptionTier,
} from '@/store/subscriptions/subscriptions.types';
import { toast } from '@/utils/toast';

// Mirror GRANT_SEATS / GRANT_CREW_SEATS on the people service.
const DEFAULT_SEATS = 20;
const DEFAULT_CREW_SEATS = 50;

const VesselIdentity = ({ vessel }: { vessel: VhAdminVessel }) => (
  <AdminEntityIdentity
    name={vessel.vessel_name}
    image={vessel.image}
    details={[vessel.make, vessel.model].filter(Boolean).join(' ')}
  />
);

const VesselOwner = ({ vessel }: { vessel: VhAdminVessel }) => {
  const owners = useVhAdminStore((state) => state.vesselOwners);
  const holder = vhAdminVesselHolder(vessel);

  return (
    <span className={styles.muted}>
      {owners[holder] ?? holder}
      {!vessel.owner_id && ' (creator)'}
    </span>
  );
};

const useIsGranted = (vessel: VhAdminVessel) =>
  useVhAdminStore((state) => !!state.vesselPlans[vessel.id]?.manually_granted);

const PlanSeats = ({ vessel }: { vessel: VhAdminVessel }) => {
  const plan = useVhAdminStore((state) => state.vesselPlans[vessel.id]);
  const seats = planSeatsLabel(plan);

  return <span className={styles.muted}>{seats ?? '-'}</span>;
};

const PlanBadge = ({ vessel }: { vessel: VhAdminVessel }) => {
  const granted = useIsGranted(vessel);

  return (
    <AdminBadge tone={isPaidTier(vessel.subscription_tier) ? 'success' : 'neutral'}>
      {tierLabel(vessel.subscription_tier ?? SubscriptionTier.HARBOR)}
      {granted && ' · Lifetime'}
    </AdminBadge>
  );
};

type GrantModalProps = {
  vessel: VhAdminVessel;
  onClose: () => void;
};

const GrantModal = ({ vessel, onClose }: GrantModalProps) => {
  const grantSubscription = useVhAdminStore((state) => state.grantSubscription);
  const owners = useVhAdminStore((state) => state.vesselOwners);
  const granted = useIsGranted(vessel);
  const [tier, setTier] = useState<PaidSubscriptionTier>(
    granted && isPaidTier(vessel.subscription_tier)
      ? (vessel.subscription_tier as PaidSubscriptionTier)
      : SubscriptionTier.COMMAND,
  );
  const [seats, setSeats] = useState(DEFAULT_SEATS);
  const [crewSeats, setCrewSeats] = useState(DEFAULT_CREW_SEATS);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const holder = vhAdminVesselHolder(vessel);

  const submit = async () => {
    if (!Number.isInteger(seats) || seats < 1) {
      setError('At least one management seat is required');
      return;
    }
    if (!Number.isInteger(crewSeats) || crewSeats < 0) {
      setError("Crew seats can't be negative");
      return;
    }

    setError('');
    setSaving(true);
    try {
      await grantSubscription({
        person_id: holder,
        service_id: vessel.id,
        tier,
        seats,
        crew_seats: crewSeats,
        vessel_name: vessel.vessel_name,
      });
      toast.success(
        granted
          ? `${vessel.vessel_name}'s lifetime plan changed to ${tierLabel(tier)}`
          : `Lifetime ${tierLabel(tier)} plan granted to ${vessel.vessel_name}`,
      );
      onClose();
    } catch (err) {
      console.error('Failed to grant a lifetime plan:', err);
      setError(
        isSeatsBelowOwnerGroup(err)
          ? 'The owner and every co-owner need a management seat. Add more management seats'
          : "Couldn't grant the plan. Try again",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminModal
      title={
        granted
          ? `Change ${vessel.vessel_name}'s lifetime plan`
          : `Grant lifetime plan to ${vessel.vessel_name}`
      }
      onClose={onClose}
      footer={
        <>
          <AdminButton onClick={onClose} disabled={saving}>
            Cancel
          </AdminButton>
          <AdminButton tone="primary" onClick={submit} disabled={saving}>
            {granted ? 'Change lifetime plan' : 'Grant lifetime plan'}
          </AdminButton>
        </>
      }
    >
      <p className={styles.hint}>
        Replaces the current plan with one that never expires and is never billed. The plan is held
        by <strong>{owners[holder] ?? holder}</strong>.
      </p>
      {!granted && isPaidTier(vessel.subscription_tier) && (
        <p className={styles.hint}>
          This vessel is on <strong>{tierLabel(vessel.subscription_tier ?? '')}</strong> now. If it
          pays through Stripe, that subscription is cancelled straight away, with no refund for the
          time left.
        </p>
      )}
      <p className={styles.hint}>
        Everyone keeps their seat. If the plan has fewer seats than are taken, members lose theirs
        starting from the lowest role: custom roles, then mate, chef and steward, then captains.
        Someone who loses a management seat moves to a free crew seat if there is one, otherwise to
        Harbor. The owner and co-owners keep theirs.
      </p>
      <p className={styles.hint}>
        The vessel can&apos;t change, buy over or cancel a lifetime plan. To change its seats,
        change it here, or revoke it to put the vessel back on Harbor.
      </p>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>Plan</span>
        <AdminSelect
          value={tier}
          onChange={(value) => setTier(value as PaidSubscriptionTier)}
          label="Plan"
        >
          {PAID_TIERS.map((paid) => (
            <option key={paid} value={paid}>
              {tierLabel(paid)}
            </option>
          ))}
        </AdminSelect>
      </label>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>Management seats</span>
        <input
          type="number"
          min={1}
          className={styles.input}
          value={seats}
          onChange={(event) => setSeats(event.target.valueAsNumber)}
        />
      </label>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>Crew seats</span>
        <input
          type="number"
          min={0}
          className={styles.input}
          value={crewSeats}
          onChange={(event) => setCrewSeats(event.target.valueAsNumber)}
        />
      </label>

      {error && <p className={styles.error}>{error}</p>}
    </AdminModal>
  );
};

type RevokeModalProps = {
  vessel: VhAdminVessel;
  onClose: () => void;
};

const RevokeModal = ({ vessel, onClose }: RevokeModalProps) => {
  const revokeSubscription = useVhAdminStore((state) => state.revokeSubscription);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const confirm = async () => {
    setError('');
    setSaving(true);
    try {
      await revokeSubscription(vessel.id);
      toast.success(`Lifetime plan revoked from ${vessel.vessel_name}`);
      onClose();
    } catch (err) {
      console.error('Failed to revoke a lifetime plan:', err);
      setError("Couldn't revoke the plan. Try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminModal
      title={`Revoke ${vessel.vessel_name}'s lifetime plan?`}
      onClose={onClose}
      footer={
        <>
          <AdminButton onClick={onClose} disabled={saving}>
            Cancel
          </AdminButton>
          <AdminButton tone="danger" onClick={confirm} disabled={saving}>
            Revoke lifetime plan
          </AdminButton>
        </>
      }
    >
      <p className={styles.hint}>
        The vessel goes back to <strong>Harbor</strong> straight away and loses everything its{' '}
        <strong>{tierLabel(vessel.subscription_tier ?? '')}</strong> plan unlocked. Everyone aboard
        moves to a Harbor seat, and co-owners who lose their management seat are told.
      </p>
      <p className={styles.hint}>
        Nothing is billed or refunded. Afterwards the vessel can buy a plan itself.
      </p>
      {error && <p className={styles.error}>{error}</p>}
    </AdminModal>
  );
};

type PlanActionsProps = {
  vessel: VhAdminVessel;
  onGrant: () => void;
  onRevoke: () => void;
  /** Set for super admins only, who alone may delete a vessel they don't own. */
  onDelete?: () => void;
};

const DeleteVesselButton = ({
  vessel,
  onDelete,
}: {
  vessel: VhAdminVessel;
  onDelete: () => void;
}) => (
  <AdminButton
    size="sm"
    tone="danger"
    onClick={onDelete}
    aria-label={`Delete ${vessel.vessel_name}`}
  >
    <FiTrash2 />
    Delete
  </AdminButton>
);

const PlanActions = ({ vessel, onGrant, onRevoke, onDelete }: PlanActionsProps) => {
  const granted = useIsGranted(vessel);

  if (!granted) {
    return (
      <div className={styles.actions}>
        <AdminButton
          size="sm"
          onClick={onGrant}
          aria-label={`Grant lifetime plan to ${vessel.vessel_name}`}
        >
          <FiGift />
          Grant lifetime plan
        </AdminButton>
        {onDelete && <DeleteVesselButton vessel={vessel} onDelete={onDelete} />}
      </div>
    );
  }

  return (
    <div className={styles.actions}>
      <AdminButton
        size="sm"
        onClick={onGrant}
        aria-label={`Change ${vessel.vessel_name}'s lifetime plan`}
      >
        <FiEdit2 />
        Change lifetime plan
      </AdminButton>
      <AdminButton
        size="sm"
        tone="danger"
        onClick={onRevoke}
        aria-label={`Revoke ${vessel.vessel_name}'s lifetime plan`}
      >
        <FiXCircle />
        Revoke
      </AdminButton>
      {onDelete && <DeleteVesselButton vessel={vessel} onDelete={onDelete} />}
    </div>
  );
};

export const AllVesselsPage = () => {
  const {
    vessels,
    vesselsCount,
    vesselsPage,
    vesselsLoading,
    vesselOwners,
    fetchVessels,
    deleteVessel,
  } = useVhAdminStore();
  const isSuperAdmin = isSuperAdminRole(useVhAdminAccessStore((state) => state.role));
  const [search, setSearch] = useState('');
  const [granting, setGranting] = useState<VhAdminVessel | null>(null);
  const [revoking, setRevoking] = useState<VhAdminVessel | null>(null);
  const [deleting, setDeleting] = useState<VhAdminVessel | null>(null);
  const deleteHandler = (vessel: VhAdminVessel) =>
    isSuperAdmin ? () => setDeleting(vessel) : undefined;
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      fetchVessels({ page: 1, search });
      return;
    }

    const timeout = setTimeout(() => {
      fetchVessels({ page: 1, search });
    }, 300);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <div>
      <div className={styles.header}>
        <h1 className={styles.title}>All Vessels</h1>
        <AdminSearchInput value={search} onChange={setSearch} placeholder="Search by name…" />
      </div>

      {vesselsLoading && vessels.length === 0 ? (
        <div className={styles.loading}>
          <AdminSpinner />
        </div>
      ) : vessels.length === 0 ? (
        <div className={styles.empty}>No vessels found</div>
      ) : (
        <>
          <div className={styles.desktopOnly}>
            <AdminTable>
              <AdminTableHead>
                <AdminTableHeadCell>Vessel</AdminTableHeadCell>
                <AdminTableHeadCell>Owner</AdminTableHeadCell>
                <AdminTableHeadCell>Plan</AdminTableHeadCell>
                <AdminTableHeadCell>Seats</AdminTableHeadCell>
                <AdminTableHeadCell>{''}</AdminTableHeadCell>
              </AdminTableHead>
              <tbody>
                {vessels.map((vessel) => (
                  <AdminTableRow key={vessel.id}>
                    <AdminTableCell>
                      <VesselIdentity vessel={vessel} />
                    </AdminTableCell>
                    <AdminTableCell>
                      <VesselOwner vessel={vessel} />
                    </AdminTableCell>
                    <AdminTableCell>
                      <PlanBadge vessel={vessel} />
                    </AdminTableCell>
                    <AdminTableCell>
                      <PlanSeats vessel={vessel} />
                    </AdminTableCell>
                    <AdminTableCell>
                      <PlanActions
                        vessel={vessel}
                        onGrant={() => setGranting(vessel)}
                        onRevoke={() => setRevoking(vessel)}
                        onDelete={deleteHandler(vessel)}
                      />
                    </AdminTableCell>
                  </AdminTableRow>
                ))}
              </tbody>
            </AdminTable>
          </div>

          <div className={styles.mobileOnly}>
            <AdminCardList>
              {vessels.map((vessel) => (
                <AdminCard key={vessel.id}>
                  <div className={styles.cardHeader}>
                    <VesselIdentity vessel={vessel} />
                    <PlanBadge vessel={vessel} />
                  </div>
                  <div className={styles.cardMeta}>
                    <VesselOwner vessel={vessel} />
                  </div>
                  <div className={styles.cardMeta}>
                    <PlanSeats vessel={vessel} />
                  </div>
                  <PlanActions
                    vessel={vessel}
                    onGrant={() => setGranting(vessel)}
                    onRevoke={() => setRevoking(vessel)}
                    onDelete={deleteHandler(vessel)}
                  />
                </AdminCard>
              ))}
            </AdminCardList>
          </div>

          <AdminPagination
            page={vesselsPage}
            pageSize={VH_ADMIN_PAGE_SIZE}
            total={vesselsCount}
            onPageChange={(page) => fetchVessels({ page })}
          />
        </>
      )}

      {granting && <GrantModal vessel={granting} onClose={() => setGranting(null)} />}
      {revoking && <RevokeModal vessel={revoking} onClose={() => setRevoking(null)} />}
      {deleting && (
        <AdminForceDeleteModal
          kind="vessel"
          name={deleting.vessel_name}
          creatorName={vesselOwners[vhAdminVesselHolder(deleting)] ?? null}
          userCreated
          consequences="Everyone on it loses access, and everything recorded on it is deleted with it."
          onClose={() => setDeleting(null)}
          onConfirm={async () => {
            await deleteVessel(deleting.id);
            toast.success(`“${deleting.vessel_name}” has been deleted.`);
          }}
        />
      )}
    </div>
  );
};
